import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import useAuth from '../hooks/useAuth';
import { useLocation, useNavigate } from 'react-router-dom';
import { destinationFrom } from '../utils/tabDeepLink';
import { TENANT_PASSWORD_HINT, tenantPasswordError } from '../utils/tenantPassword';
import api from '../api';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, authLoading, authError, clearAuthError, isAuthenticated, logout } = useAuth();

  const [authTab, setAuthTab] = useState(0);
  const [form, setForm] = useState({
    email: '',
    password: '',
  });
  const [registerForm, setRegisterForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [resetMode, setResetMode] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSending, setResetSending] = useState(false);
  const [resetMessage, setResetMessage] = useState('');

  const destination = destinationFrom(location.state?.from);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(destination, { replace: true });
    }
  }, [isAuthenticated, navigate, destination]);

  // Clear session on auth errors
  useEffect(() => {
    if (authError && authError.includes('Insufficient role permissions')) {
      // Clear any stale session data
      logout();
    }
  }, [authError, logout]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (error) setError('');
    if (authError) clearAuthError();
  };

  const handleRegisterFieldChange = (event) => {
    const { name, value } = event.target;
    setRegisterForm((prev) => ({ ...prev, [name]: value }));
    if (error) setError('');
    if (authError) clearAuthError();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.email || !form.password) {
      setError('Email and password are required.');
      return;
    }

    try {
      await login(form.email, form.password);
      navigate(destination, { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    }
  };

  const openReset = () => {
    setResetEmail(form.email);
    setResetMessage('');
    setError('');
    if (authError) clearAuthError();
    setResetMode(true);
  };

  const closeReset = () => {
    setResetMode(false);
    setResetMessage('');
    setError('');
  };

  const handleResetSubmit = async (event) => {
    event.preventDefault();
    if (!resetEmail.trim()) {
      setError('Enter the email you sign in with.');
      return;
    }
    setResetSending(true);
    setError('');
    try {
      const { data } = await api.post('/api/auth/password-reset', { email: resetEmail });
      setResetMessage(data?.message || 'If an account exists for that email, we sent a link to reset your password.');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send the reset email. Please try again.');
    } finally {
      setResetSending(false);
    }
  };

  const handleRegisterSubmit = async (event) => {
    event.preventDefault();
    const { email, password, confirmPassword, displayName } = registerForm;
    if (!email || !password) {
      setError('Email and password are required.');
      return;
    }
    const passwordError = tenantPasswordError(password);
    if (passwordError) {
      setError(`${passwordError}.`);
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    try {
      await register({ email, password, displayName });
      navigate(destination, { replace: true });
    } catch (err) {
      if (err.accountCreated) {
        setForm({ email, password: '' });
        setAuthTab(0);
      }
      setError(err.message || 'Registration failed. Please try again.');
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper elevation={4} sx={{ p: 5, width: '100%' }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              ZenRentFlow
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {authTab === 0
                ? resetMode
                  ? 'Reset your password.'
                  : 'Sign in to your ZenRentFlow account.'
                : 'Create a landlord account. Your data stays scoped to properties you add.'}
            </Typography>
          </Box>

          <Tabs
            value={authTab}
            onChange={(_, v) => {
              setAuthTab(v);
              setResetMode(false);
              setResetMessage('');
              setError('');
              clearAuthError();
            }}
            variant="fullWidth"
            sx={{ mb: 2 }}
          >
            <Tab label="Sign in" />
            <Tab label="Create account" />
          </Tabs>

          {authTab === 0 && resetMode ? (
          <form onSubmit={handleResetSubmit} noValidate>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {resetMessage ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                {resetMessage} The link expires in one hour. Check your spam folder if you don't see it.
              </Alert>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Enter the email you sign in with and we&apos;ll send you a link to choose a new password.
              </Typography>
            )}

            <TextField
              name="resetEmail"
              label="Email"
              type="email"
              value={resetEmail}
              onChange={(event) => {
                setResetEmail(event.target.value);
                if (error) setError('');
              }}
              fullWidth
              margin="normal"
              required
              autoComplete="email"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlinedIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={resetSending}
              sx={{ mt: 3 }}
            >
              {resetSending ? 'Sending...' : 'Send reset link'}
            </Button>
            <Button fullWidth onClick={closeReset} sx={{ mt: 1 }}>
              Back to sign in
            </Button>
          </form>
          ) : authTab === 0 ? (
          <form onSubmit={handleSubmit} noValidate>
            {(error || authError) && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error || authError}
              </Alert>
            )}

            <TextField
              name="email"
              label="Email"
              type="email"
              value={form.email}
              onChange={handleChange}
              fullWidth
              margin="normal"
              required
              autoComplete="email"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlinedIcon />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={form.password}
              onChange={handleChange}
              fullWidth
              margin="normal"
              required
              autoComplete="current-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ textAlign: 'right' }}>
              <Button size="small" onClick={openReset} sx={{ textTransform: 'none' }}>
                Forgot password?
              </Button>
            </Box>

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              startIcon={<LoginOutlinedIcon />}
              disabled={authLoading}
              sx={{ mt: 3 }}
            >
              {authLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
          ) : (
          <form onSubmit={handleRegisterSubmit} noValidate>
            {(error || authError) && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error || authError}
              </Alert>
            )}

            <TextField
              name="displayName"
              label="Your name (optional)"
              value={registerForm.displayName}
              onChange={handleRegisterFieldChange}
              fullWidth
              margin="normal"
              autoComplete="name"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineIcon />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              name="email"
              label="Email"
              type="email"
              value={registerForm.email}
              onChange={handleRegisterFieldChange}
              fullWidth
              margin="normal"
              required
              autoComplete="email"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlinedIcon />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={registerForm.password}
              onChange={handleRegisterFieldChange}
              fullWidth
              margin="normal"
              required
              autoComplete="new-password"
              helperText={TENANT_PASSWORD_HINT}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              name="confirmPassword"
              label="Confirm password"
              type={showPassword ? 'text' : 'password'}
              value={registerForm.confirmPassword}
              onChange={handleRegisterFieldChange}
              fullWidth
              margin="normal"
              required
              autoComplete="new-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              startIcon={<LoginOutlinedIcon />}
              disabled={authLoading}
              sx={{ mt: 3 }}
            >
              {authLoading ? 'Creating account...' : 'Create account'}
            </Button>
          </form>
          )}

          {authTab === 0 && (
            <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary" align="center" gutterBottom>
                Are you a tenant?
              </Typography>
              <Button
                component="a"
                href="/tenant-portal"
                fullWidth
                variant="outlined"
                size="medium"
                startIcon={<PersonOutlineIcon />}
                sx={{ mt: 1 }}
              >
                Sign in to Tenant Portal
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default LoginPage;

